import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface AgentConfigFlat {
  systemPrompt: string;
  model: string;
  temperature: number;
  llmProvider?: string;
  agentType?: string;
}

export interface FleetMember {
  agentName: string;
  id?: string | null;
  displayName?: string;
  role?: string;
  agentType?: string;
}

export interface AgentFleet {
  agents: FleetMember[];
  orchestrationMode?: string;
  orchestrationLabel?: string;
}

export interface AgentMembership {
  parentAgentName: string;
  parentId?: string;
  role?: string;
}

export interface Agent {
  id: string;
  agentName: string;
  name: string;
  description: string;
  scope: 'global' | 'organisation' | 'application' | 'system';
  version: string;
  versionStatus: 'draft' | 'sandbox' | 'published';
  status: string;
  agentType?: string;
  llmProvider?: string;
  llmModel?: string;
  visibility?: string;
  applicationCount?: number;
  createdAt?: string;
  updatedAt?: string;
  readOnly?: boolean;
  fleet?: AgentFleet | null;
  membership?: AgentMembership | null;
}

export interface AgentDetail extends Agent {
  stats: {
    totalSessions: number;
    totalExchanges: number;
    avgResponseTime: number;
    activeApplications: number;
  };
  versions: VersionEntry[];
  applications: AgentApplication[];
  config?: AgentConfigFlat;
  fleet?: AgentFleet | null;
  membership?: AgentMembership | null;
  capabilities?: string[];
}

export interface VersionEntry {
  version: string;
  status: 'draft' | 'sandbox' | 'published';
  date: string;
  description?: string;
  changeCount?: number;
}

export interface AgentApplication {
  id: string;
  name: string;
  appId: string;
  status: string;
}

function mapAgent(item: Record<string, unknown>, index = 0): Agent {
  const agentName = String(item.agentName ?? item.name ?? `agent-${index}`);
  const scopeRaw = String(item.scope ?? '').toLowerCase();
  const visibility = String(item.visibility ?? 'private').toLowerCase();
  let scope: Agent['scope'] = 'organisation';
  if (scopeRaw === 'system' || visibility === 'system') {
    scope = 'system';
  } else if (scopeRaw === 'global' || visibility === 'public' || visibility === 'global') {
    scope = 'global';
  } else if (scopeRaw === 'application' || visibility === 'application') {
    scope = 'application';
  }

  const versionStatus = String(
    item.versionStatus ?? 'published',
  ).toLowerCase() as Agent['versionStatus'];

  return {
    id: String(item.id ?? agentName),
    agentName,
    name: String(item.name ?? agentName),
    description: String(item.description ?? ''),
    scope,
    version: String(item.version ?? item.llmModel ?? '—'),
    versionStatus: ['draft', 'sandbox', 'published'].includes(versionStatus)
      ? versionStatus
      : 'published',
    status: String(item.status ?? 'active'),
    agentType: item.agentType ? String(item.agentType) : undefined,
    llmProvider: item.llmProvider ? String(item.llmProvider) : undefined,
    llmModel: item.llmModel ? String(item.llmModel) : undefined,
    visibility,
    applicationCount:
      typeof item.applicationCount === 'number'
        ? item.applicationCount
        : undefined,
    createdAt: item.createdAt ? String(item.createdAt) : undefined,
    updatedAt: item.updatedAt ? String(item.updatedAt) : undefined,
    readOnly:
      Boolean(item.readOnly) || scope === 'global' || scope === 'system',
    fleet: mapFleet(item.fleet),
    membership: mapMembership(item.membership),
  };
}

function unwrapAgents(response: unknown): Agent[] {
  const normalized = normalizeKeys<{
    agents?: Array<Record<string, unknown>>;
    response?: { agents?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>;
  }>(response);

  const rawList =
    normalized.agents ??
    (Array.isArray(normalized.response)
      ? normalized.response
      : normalized.response?.agents) ??
    [];

  return rawList.map((item, index) => mapAgent(item, index));
}

function unwrapAgentDetail(response: unknown): AgentDetail {
  const normalized = normalizeKeys<Record<string, unknown>>(response);
  const base = mapAgent(normalized);
  const statsRaw = (normalized.stats as Record<string, unknown>) || {};
  const versionsRaw = Array.isArray(normalized.versions)
    ? (normalized.versions as Array<Record<string, unknown>>)
    : [];
  const appsRaw = Array.isArray(normalized.applications)
    ? (normalized.applications as Array<Record<string, unknown>>)
    : [];
  const configRaw = (normalized.config as Record<string, unknown>) || {};

  return {
    ...base,
    stats: {
      totalSessions: Number(statsRaw.totalSessions ?? 0),
      totalExchanges: Number(statsRaw.totalExchanges ?? 0),
      avgResponseTime: Number(statsRaw.avgResponseTime ?? 0),
      activeApplications: Number(statsRaw.activeApplications ?? 0),
    },
    versions: versionsRaw.map((v) => ({
      version: String(v.version ?? ''),
      status: (['draft', 'sandbox', 'published'].includes(String(v.status))
        ? String(v.status)
        : 'published') as VersionEntry['status'],
      date: String(v.date ?? ''),
      description: v.description ? String(v.description) : undefined,
      changeCount:
        typeof v.changeCount === 'number' ? v.changeCount : undefined,
    })),
    applications: appsRaw.map((a) => ({
      id: String(a.id ?? ''),
      name: String(a.name ?? ''),
      appId: String(a.appId ?? ''),
      status: String(a.status ?? ''),
    })),
    config: {
      systemPrompt: String(configRaw.systemPrompt ?? ''),
      model: String(configRaw.model ?? 'gpt-4o'),
      temperature: Number(configRaw.temperature ?? 0.7),
      llmProvider: configRaw.llmProvider
        ? String(configRaw.llmProvider)
        : undefined,
      agentType: configRaw.agentType ? String(configRaw.agentType) : undefined,
    },
    fleet: mapFleet(normalized.fleet),
    membership: mapMembership(normalized.membership),
    capabilities: Array.isArray(normalized.capabilities)
      ? (normalized.capabilities as unknown[]).map((c) => String(c))
      : undefined,
  };
}

function mapFleet(raw: unknown): AgentFleet | null | undefined {
  if (raw == null) return raw as null | undefined;
  if (typeof raw !== 'object') return undefined;
  const fleet = raw as Record<string, unknown>;
  const agentsRaw = Array.isArray(fleet.agents) ? fleet.agents : [];
  return {
    agents: agentsRaw.map((entry) => {
      if (typeof entry === 'string') {
        return { agentName: entry, displayName: entry };
      }
      const m = entry as Record<string, unknown>;
      const agentName = String(m.agentName ?? m.name ?? '');
      return {
        agentName,
        id: m.id != null ? String(m.id) : null,
        displayName: String(m.displayName ?? agentName),
        role: m.role ? String(m.role) : undefined,
        agentType: m.agentType ? String(m.agentType) : undefined,
      };
    }),
    orchestrationMode: fleet.orchestrationMode
      ? String(fleet.orchestrationMode)
      : undefined,
    orchestrationLabel: fleet.orchestrationLabel
      ? String(fleet.orchestrationLabel)
      : undefined,
  };
}

function mapMembership(raw: unknown): AgentMembership | null | undefined {
  if (raw == null) return raw as null | undefined;
  if (typeof raw !== 'object') return undefined;
  const m = raw as Record<string, unknown>;
  const parentAgentName = String(m.parentAgentName ?? '');
  if (!parentAgentName) return null;
  return {
    parentAgentName,
    parentId: m.parentId ? String(m.parentId) : undefined,
    role: m.role ? String(m.role) : undefined,
  };
}

export const agentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<Agent[], { scope?: string } | void>({
      query: () => '/portal/agents',
      transformResponse: (response: unknown) => unwrapAgents(response),
      providesTags: ['Agent'],
    }),

    getAgent: builder.query<AgentDetail, string>({
      query: (id) => `/portal/agents/${encodeURIComponent(id)}`,
      transformResponse: (response: unknown) => unwrapAgentDetail(response),
      providesTags: (_r, _e, id) => [{ type: 'Agent', id }],
    }),

    createAgent: builder.mutation<
      AgentDetail,
      { name: string; description?: string }
    >({
      query: (body) => ({
        url: '/portal/agents',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapAgentDetail(response),
      invalidatesTags: ['Agent'],
    }),

    updateAgentConfig: builder.mutation<
      AgentDetail,
      { id: string; config: Record<string, unknown> }
    >({
      query: ({ id, config }) => ({
        url: `/portal/agents/${encodeURIComponent(id)}/config`,
        method: 'PUT',
        body: { config },
      }),
      transformResponse: (response: unknown) => unwrapAgentDetail(response),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Agent', id }, 'Agent'],
    }),

    publishAgent: builder.mutation<AgentDetail, string>({
      query: (id) => ({
        url: `/portal/agents/${encodeURIComponent(id)}/publish`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => unwrapAgentDetail(response),
      invalidatesTags: (_r, _e, id) => [{ type: 'Agent', id }, 'Agent'],
    }),

    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({
        url: `/portal/agents/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Agent'],
    }),
  }),
});

export const {
  useGetAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentConfigMutation,
  usePublishAgentMutation,
  useDeleteAgentMutation,
} = agentsApi;
